import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';

import { cn } from '@heidi/utils';

import {
  avatarBadgeBaseClasses,
  avatarBadgeColorClasses,
  avatarBadgeDotSizeClasses,
  avatarBadgeIconSizeClasses,
  avatarBadgePositionClasses,
  avatarFallbackClasses,
  avatarImageClasses,
  avatarRootClasses,
} from './avatar.styles';
import type {
  AvatarFallbackProps,
  AvatarGroupCountProps,
  AvatarGroupProps,
  AvatarImageProps,
  AvatarProps,
  AvatarSize,
  RingBackground,
} from './avatar.types';

const AvatarSizeContext = React.createContext<AvatarSize>('lg');

// Module-private. `AvatarFallback` assumes string children are initials, which
// is why it truncates them to a single character at `xs`. `AvatarGroupCount`
// renders a `+N` count — not initials — so it sets this `false` to keep the
// full text. Kept off the public prop surface so no consumer can switch off the
// small-size legibility collapse.
const FallbackIsInitialsContext = React.createContext(true);

const resolveRingBackground = (token: RingBackground): string =>
  `hsl(var(--twc-${token}))`;

const COUNT_BASE_FONT_SIZE: Record<NonNullable<AvatarGroupCountProps['size']>, string> = {
  xs: '0.625rem',
  sm: '0.625rem',
  md: '0.75rem',
  lg: '0.875rem',
};

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(
  (
    {
      className,
      color,
      size = 'lg',
      shape = 'circle',
      badge,
      badgePosition = 'bottom-end',
      badgeColor = 'neutral',
      badgeRingBackground = 'surface',
      ...props
    },
    ref
  ) => {
    const root = (
      <AvatarPrimitive.Root
        ref={ref}
        data-slot="avatar"
        data-size={size}
        data-shape={shape}
        data-color={color}
        className={cn(avatarRootClasses, className)}
        {...props}
      />
    );

    // Falsy `badge` renders nothing extra, so the avatar stays byte-identical
    // to the un-badged output (no wrapper, root keeps its `overflow-hidden`).
    const showBadge = badge !== undefined && badge !== null && badge !== false;
    if (!showBadge) {
      return <AvatarSizeContext.Provider value={size}>{root}</AvatarSizeContext.Provider>;
    }

    // `badge === true` is the blank status-dot shorthand; any element (icon or
    // image) renders that content in the same dot-sized circle.
    const isDot = badge === true;
    const sizeClasses = isDot
      ? avatarBadgeDotSizeClasses[size]
      : cn(avatarBadgeDotSizeClasses[size], avatarBadgeIconSizeClasses[size]);

    return (
      <AvatarSizeContext.Provider value={size}>
        {/* Non-clipping wrapper so the badge can sit on the avatar's rim
            (the root itself stays `overflow-hidden`). */}
        <span className="relative inline-flex shrink-0">
          {root}
          <span
            data-slot="avatar-badge"
            // The status dot carries no content and is purely decorative, so it
            // is hidden from assistive tech — meaningful status must be labelled
            // at the call site (see docs). Icon/image badges keep their own
            // consumer-supplied semantics (e.g. `alt`), so are not hidden.
            aria-hidden={isDot ? true : undefined}
            className={cn(
              avatarBadgeBaseClasses,
              avatarBadgePositionClasses[badgePosition],
              avatarBadgeColorClasses[badgeColor],
              sizeClasses
            )}
            style={
              badgeRingBackground !== 'surface'
                ? ({
                    '--avatar-ring-bg': resolveRingBackground(badgeRingBackground),
                  } as React.CSSProperties)
                : undefined
            }
          >
            {isDot ? null : badge}
          </span>
        </span>
      </AvatarSizeContext.Provider>
    );
  }
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  AvatarImageProps
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    data-slot="avatar-image"
    className={cn(avatarImageClasses, className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, icon, children, ...props }, ref) => {
  const size = React.useContext(AvatarSizeContext);
  const isInitials = React.useContext(FallbackIsInitialsContext);
  // `xs` is too small to legibly fit two-character initials, so collapse
  // string children to a single character. Icon children are unaffected, and
  // non-initials content (the `+N` count chip) opts out via
  // `FallbackIsInitialsContext` — it fits at the base xs font, clamping its own
  // font-size only for longer 3+ char counts.
  const content =
    icon ??
    (isInitials && size === 'xs' && typeof children === 'string'
      ? children.charAt(0)
      : children);

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      data-slot="avatar-fallback"
      className={cn(
        avatarFallbackClasses,
        icon && '[&>svg:not([class*=size-])]:size-4',
        icon && 'group-data-[size=md]/avatar:[&>svg:not([class*=size-])]:size-3',
        icon && 'group-data-[size=sm]/avatar:[&>svg:not([class*=size-])]:size-3',
        icon && 'group-data-[size=xs]/avatar:[&>svg:not([class*=size-])]:size-3',
        className
      )}
      {...props}
    >
      {content}
    </AvatarPrimitive.Fallback>
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  function AvatarGroup(
    { className, style, ringBackground = 'surface', children, ...props },
    ref
  ) {
    return (
      <div
        ref={ref}
        data-slot="avatar-group"
        className={cn(
          'group/avatar-group flex -space-x-2 rtl:space-x-reverse',
          'has-[[data-size=md]]:-space-x-1.5 has-[[data-size=md]]:rtl:space-x-reverse',
          'has-[[data-size=sm]]:-space-x-1 has-[[data-size=sm]]:rtl:space-x-reverse',
          'has-[[data-size=xs]]:-space-x-0.5 has-[[data-size=xs]]:rtl:space-x-reverse',
          // Descendant (not direct-child) selector so a badged avatar — which is
          // wrapped in a positioning span — still picks up the separating ring.
          '[&_[data-slot=avatar]]:ring-2 [&_[data-slot=avatar]]:ring-[color:var(--avatar-ring-bg,hsl(var(--twc-surface)))]',
          className
        )}
        style={
          {
            ...style,
            '--avatar-ring-bg': resolveRingBackground(ringBackground),
          } as React.CSSProperties
        }
        {...props}
      >
        {children}
      </div>
    );
  }
);

const AvatarGroupCount = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarGroupCountProps
>(function AvatarGroupCount(
  { children, color = 'neutral', size = 'lg', shape, className, ...props },
  ref
) {
  const charCount = typeof children === 'string' ? children.length : 2;
  const base = COUNT_BASE_FONT_SIZE[size];
  const fontSize =
    charCount <= 2
      ? undefined
      : `clamp(0.5rem, calc(${base} * 3 / ${charCount}), ${base})`;

  return (
    <Avatar
      ref={ref}
      color={color}
      size={size}
      shape={shape}
      className={cn(
        'ring-2 ring-[color:var(--avatar-ring-bg,hsl(var(--twc-surface)))]',
        className
      )}
      {...props}
    >
      <FallbackIsInitialsContext.Provider value={false}>
        <AvatarFallback style={fontSize ? { fontSize } : undefined}>
          {children}
        </AvatarFallback>
      </FallbackIsInitialsContext.Provider>
    </Avatar>
  );
});

export { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage };
